import { useState } from 'react'
import { router } from '@inertiajs/react'
import { AdminLayout } from '../../../layouts/admin-layout'
import { AppHeader } from '../../../components/app-header'
import { Button } from '../../../components/ui/button'
import { Badge } from '../../../components/ui/badge'
import { Input } from '../../../components/ui/input'
import { Card, CardContent } from '../../../components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select'

interface LogEntry {
  id: number
  provider: string
  model: string | null
  endpoint: string | null
  input_tokens: number
  output_tokens: number
  cost_usd: number | null
  credits_deducted: number | null
  status_code: number
  created_at: string
  project_name: string | null
  project_id: number
  organization_name: string | null
  organization_id: number
  dev_instance_name: string | null
}

interface PagyMetadata {
  page: number
  pages: number
  count: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

interface Stats {
  total_requests: number
  total_cost_usd: number
  total_credits_deducted: number
  unique_projects: number
  by_provider: Record<string, number>
}

interface Props {
  logs: LogEntry[]
  pagy: PagyMetadata
  stats: Stats
  filters: { provider?: string; project_name?: string; organization_name?: string }
}

function providerBadgeClass(provider: string): string {
  switch (provider) {
    case 'openai': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
    case 'anthropic': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
    case 'gemini': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
    default: return ''
  }
}

function Index({ logs, pagy, stats, filters }: Props) {
  const [searchProject, setSearchProject] = useState(filters.project_name || '')
  const [searchOrg, setSearchOrg] = useState(filters.organization_name || '')

  const buildParams = (overrides: Record<string, string | null> = {}) => {
    const params: Record<string, string> = {}
    if (searchProject) params.project_name = searchProject
    if (searchOrg) params.organization_name = searchOrg
    if (filters.provider) params.provider = filters.provider

    for (const [key, value] of Object.entries(overrides)) {
      if (value === null || value === '') {
        delete params[key]
      } else {
        params[key] = value
      }
    }
    return params
  }

  const handleSearch = () => {
    router.get('/admin/api_proxy_logs', buildParams(), { preserveState: true })
  }

  const handleReset = () => {
    setSearchProject('')
    setSearchOrg('')
    router.get('/admin/api_proxy_logs', {}, { preserveState: true })
  }

  const handleProviderChange = (value: string) => {
    router.get('/admin/api_proxy_logs', buildParams({ provider: value === 'all' ? null : value }), { preserveState: true })
  }

  const handlePageChange = (page: number) => {
    router.get('/admin/api_proxy_logs', { ...buildParams(), page: String(page) }, { preserveState: true })
  }

  return (
    <>
      <AppHeader breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: 'API Proxy Logs' }]} />

      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col py-2">
            <div className="px-3">
              {/* Compact Filter Bar */}
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="text-sm font-semibold whitespace-nowrap">
                  API Proxy Logs ({pagy.count})
                </span>
                <div className="w-px h-4 bg-border" />
                <Select value={filters.provider || 'all'} onValueChange={handleProviderChange}>
                  <SelectTrigger className="h-7 text-xs w-28">
                    <SelectValue placeholder="Provider" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Providers</SelectItem>
                    <SelectItem value="openai">OpenAI</SelectItem>
                    <SelectItem value="anthropic">Anthropic</SelectItem>
                    <SelectItem value="gemini">Gemini</SelectItem>
                  </SelectContent>
                </Select>
                <div className="w-px h-4 bg-border" />
                <Input
                  placeholder="Project name..."
                  value={searchProject}
                  onChange={(e) => setSearchProject(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="h-7 text-xs w-40"
                />
                <Input
                  placeholder="Organization..."
                  value={searchOrg}
                  onChange={(e) => setSearchOrg(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="h-7 text-xs w-40"
                />
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleSearch}>
                  Search
                </Button>
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={handleReset}>
                  Reset
                </Button>
              </div>

              {/* Stats */}
              <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Card>
                  <CardContent className="p-3">
                    <p className="text-xs text-muted-foreground">Total Requests</p>
                    <p className="text-xl font-bold">{stats.total_requests.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3">
                    <p className="text-xs text-muted-foreground">Total Cost (USD)</p>
                    <p className="text-xl font-bold">${stats.total_cost_usd.toFixed(4)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3">
                    <p className="text-xs text-muted-foreground">Credits Deducted</p>
                    <p className="text-xl font-bold">{stats.total_credits_deducted.toFixed(2)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3">
                    <p className="text-xs text-muted-foreground">Unique Projects</p>
                    <p className="text-xl font-bold">{stats.unique_projects}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Table */}
              <div className="rounded-lg border border-border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Provider</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Model</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Endpoint</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Project</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Organization</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground whitespace-nowrap">In / Out</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">Cost</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">Credits</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-muted-foreground">Status</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Time</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {logs.length === 0 ? (
                        <tr>
                          <td colSpan={10} className="px-4 py-12 text-center text-muted-foreground">
                            No API proxy logs found
                          </td>
                        </tr>
                      ) : (
                        logs.map((log) => (
                          <tr key={log.id} className="hover:bg-muted/30">
                            <td className="px-3 py-2">
                              <Badge variant="secondary" className={providerBadgeClass(log.provider)}>
                                {log.provider}
                              </Badge>
                            </td>
                            <td className="px-3 py-2 text-sm whitespace-nowrap">{log.model || '—'}</td>
                            <td className="px-3 py-2 text-xs text-muted-foreground font-mono whitespace-nowrap">{log.endpoint || '—'}</td>
                            <td className="px-3 py-2 text-xs whitespace-nowrap">{log.project_name || '—'}</td>
                            <td className="px-3 py-2 text-xs whitespace-nowrap">{log.organization_name || '—'}</td>
                            <td className="px-3 py-2 text-right text-xs tabular-nums whitespace-nowrap">
                              {log.input_tokens.toLocaleString()} / {log.output_tokens.toLocaleString()}
                            </td>
                            <td className="px-3 py-2 text-right text-xs tabular-nums whitespace-nowrap">
                              {log.cost_usd ? `$${log.cost_usd.toFixed(4)}` : '—'}
                            </td>
                            <td className="px-3 py-2 text-right text-xs tabular-nums">
                              {log.credits_deducted && log.credits_deducted > 0 ? log.credits_deducted.toFixed(2) : '—'}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <Badge variant={log.status_code === 200 ? 'default' : 'destructive'} className="text-xs">
                                {log.status_code}
                              </Badge>
                            </td>
                            <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                              {new Date(log.created_at).toLocaleString()}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {pagy.pages > 1 && (
                <div className="mt-3 flex items-center justify-between text-sm text-muted-foreground">
                  <span>Showing {pagy.from}–{pagy.to} of {pagy.count}</span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={!pagy.prev} onClick={() => pagy.prev && handlePageChange(pagy.prev)}>
                      Previous
                    </Button>
                    <Button variant="outline" size="sm" disabled={!pagy.next} onClick={() => pagy.next && handlePageChange(pagy.next)}>
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

Index.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Index
